import classNames from 'classnames';
import Skeleton from 'react-loading-skeleton';
import { UncontrolledTooltip } from 'reactstrap';
import { NoDataIcon as noDataIcon } from '@/shared/ui';
import { convertToMomentJs } from '@/shared/lib/aiUtils';
import { HR_TYPE } from '@/widgets/hr-summary/ui/helper';
import HrsEventPreviewStrip from '@/widgets/hr-summary/ui/hrsEventPreviewStrip';
import { IHrsInDate } from '@/shared/model';
import subtractIcon from '../subtract-icon.svg';
import useDailySummaryHrsThumbnail from './useDailySummaryHrsThumbnail';

export type DailySummaryHrsThumbnailProps = {
  timezoneOffset?: number;
  ecgDataMap?: any;
  studyId?: any;
  profileId?: any;
};

/**
 * Renders the right-hand list of minimum and maximum heart-rate readings for the selected day, each paired with an ECG thumbnail strip. State comes from useDailySummaryHrsThumbnail; the header switches between the Min HR and Max HR tabs, exposes channel 1-3 thumbnail toggles once either list has values, and the body falls back to a no-data panel when both lists are empty.
 *
 * @param props - Study id, profile id, timezone offset and ECG data map for the day being summarised.
 * @returns The rendered min/max HR list with its tab header, channel toggles and thumbnail items.
 */
const DailySummaryHrsThumbnail = ({
  timezoneOffset,
  ecgDataMap,
  studyId,
  profileId,
}: DailySummaryHrsThumbnailProps) => {
  const {
    tabHrActive,
    setTabHrActive,
    setMinHrTabActive,
    channelsThumbnail,
    toggleChannel,
    minHrsInDate,
    maxHrsInDate,
    hrData,
    isLoading,
    highPassThumbnail,
    gainThumbnail,
    activeItemRef,
    handleUpdateValueMinMaxHr,
  } = useDailySummaryHrsThumbnail({ timezoneOffset });

  // This function is used to render the items for min and max HR
  /**
   * Builds the clickable list items for one heart-rate series, each pairing a reading with its preview strip. Entries whose start or stop timestamps fail moment validation are dropped, an item is marked active when it matches the stored min or max HR, readings the user has not overridden carry a system-suggested tooltip, and a skeleton stands in while ECG data loads; clicking or pressing Enter commits the reading through handleUpdateValueMinMaxHr.
   *
   * @param hrsInDate - Heart-rate readings for the selected day, in display order.
   * @param isMinHr - Whether the series is the minimum-HR list rather than the maximum-HR one.
   * @returns An array of HR item buttons, holding null where an entry had invalid timestamps.
   */
  const renderHrItems = (hrsInDate: IHrsInDate[], isMinHr: boolean) =>
    hrsInDate.map(
      (
        {
          value,
          start,
          stop,
          // userUpdated is written at runtime (see updatedFields.suggestHrsOnBeat above) but
          // IHrsInDate (shared/model/heartRate.ts) doesn't declare it.
          userUpdated,
        }: IHrsInDate & { userUpdated?: boolean },
        index,
      ) => {
        const startOffset = convertToMomentJs(start, timezoneOffset);
        const stopOffset = convertToMomentJs(stop, timezoneOffset);

        if (!startOffset.isValid() || !stopOffset.isValid()) {
          return null;
        }

        // check if the hrData is active
        const isActive = isMinHr
          ? hrData?.minHr?.time === start && Number(hrData?.minHr?.value) === Number(value)
          : hrData?.maxHr?.time === start && Number(hrData?.maxHr?.value) === Number(value);

        return (
          <button
            key={`hr-item-btn-${isMinHr ? 'min' : 'max'}-${start}`}
            ref={isActive ? activeItemRef : null}
            id={`hr-item-btn-${isMinHr ? 'min' : 'max'}-${index}`}
            className={classNames(isActive ? 'active' : '', 'mct-hrs-item')}
            onClick={() => {
              handleUpdateValueMinMaxHr(isMinHr ? HR_TYPE.MIN_HR : HR_TYPE.MAX_HR, value, start);
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                handleUpdateValueMinMaxHr(isMinHr ? HR_TYPE.MIN_HR : HR_TYPE.MAX_HR, value);
              }
            }}
          >
            <div className="item-header-container">
              <div className="item-header">
                <div className="radio-button">
                  {isActive && <div className="radio-button-inner --active" />}
                </div>
                <div className="time">
                  {convertToMomentJs(start, timezoneOffset).format('HH:mm:ss')}
                </div>
                <div className="wrap" />
                <div className="hr">
                  <div className="title">HR:</div>
                  <div className="value">{value}</div>
                </div>
              </div>
              {!userUpdated && (
                <span id={`bt-subtract-${index}`} className="icon-subtract">
                  <img src={subtractIcon} alt="subtract" />
                  <UncontrolledTooltip
                    target={`bt-subtract-${index}`}
                    className="custom-tooltip"
                    placement="bottom"
                    delay={{ show: 0, hide: 0 }}
                    hideArrow
                  >
                    System suggested
                  </UncontrolledTooltip>
                </span>
              )}
            </div>
            <div className="item-content">
              {isLoading ? (
                <div className="thumbnail">
                  <Skeleton height={118} width={448} style={{ borderRadius: 0 }} />
                </div>
              ) : (
                <div className="thumbnail">
                  <HrsEventPreviewStrip
                    id={`id-hrs-${isMinHr ? 'min' : 'max'}-${value}`}
                    ecgDataMap={ecgDataMap}
                    start={startOffset}
                    stop={stopOffset}
                    studyId={studyId}
                    profileId={profileId}
                    channels={channelsThumbnail}
                    highPass={highPassThumbnail}
                    gain={gainThumbnail}
                    width={448}
                    height={118}
                    timezoneOffset={timezoneOffset}
                    isLoading={isLoading}
                  />
                </div>
              )}
            </div>
          </button>
        );
      },
    );

  return (
    <div id="right-column" className="mct-daily-list-hrs">
      <div className="mct-hrs-title">
        <div className="mct-hrs-tab">
          {[HR_TYPE.MIN_HR, HR_TYPE.MAX_HR].map((tab) => (
            <button
              id={`hr-tab-btn-${tab}`}
              key={tab}
              className={classNames(tabHrActive === tab ? '--active' : '', 'btn-tab-hr')}
              onClick={() => {
                setTabHrActive(tab);
                setMinHrTabActive(tab === HR_TYPE.MIN_HR);
              }}
            >
              {tab === HR_TYPE.MIN_HR ? 'Min HR' : 'Max HR'}
            </button>
          ))}
        </div>
        {minHrsInDate[0]?.value !== undefined || maxHrsInDate[0]?.value !== undefined ? (
          <div className="mct-hrs-chanel">
            <span className="title">Thumbnail:</span>
            {[1, 2, 3].map((channel, index) => (
              <button
                id={`thumbnail-channel-btn-${index}`}
                key={channel}
                className={classNames(
                  channelsThumbnail.includes(channel) ? '--active' : '',
                  'btn-ch-hr',
                )}
                onClick={() => {
                  toggleChannel(channel);
                }}
              >
                CH
                {channel}
              </button>
            ))}
          </div>
        ) : null}
      </div>
      <div className="mct-hrs-list">
        {minHrsInDate[0]?.value === undefined || maxHrsInDate[0]?.value === undefined ? (
          <div className="no-data">
            <div className="no-data__content">
              <img className="no-data__icon" src={noDataIcon} alt="No data icon" />
              <div className="no-data__text">There is no data to display</div>
            </div>
          </div>
        ) : tabHrActive === HR_TYPE.MIN_HR ? (
          renderHrItems(minHrsInDate, true)
        ) : (
          renderHrItems(maxHrsInDate, false)
        )}
      </div>
    </div>
  );
};

export default DailySummaryHrsThumbnail;
